import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useSuperAdmin } from './useSuperAdmin';
import { useSubscriptionPlans } from './useSubscriptionPlans';

const GRACE_PERIOD_DAYS = 7;

interface GracePeriodInfo {
  isWithinGracePeriod: boolean;
  gracePeriodDaysRemaining: number;
  daysOverdue: number;
  shouldForcePayment: boolean;
}

const calculateGracePeriod = (expiresAt: string | null): GracePeriodInfo => {
  if (!expiresAt) {
    return { 
      isWithinGracePeriod: false, 
      gracePeriodDaysRemaining: 0, 
      daysOverdue: 0,
      shouldForcePayment: false 
    };
  }
  
  const expiryDate = new Date(expiresAt);
  const now = new Date();
  const gracePeriodEnd = new Date(expiryDate);
  gracePeriodEnd.setDate(gracePeriodEnd.getDate() + GRACE_PERIOD_DAYS);
  
  const msPerDay = 1000 * 60 * 60 * 24;
  const daysUntilExpiry = Math.ceil((expiryDate.getTime() - now.getTime()) / msPerDay);
  const daysUntilGraceEnd = Math.ceil((gracePeriodEnd.getTime() - now.getTime()) / msPerDay);
  
  // Subscription hasn't expired yet
  if (daysUntilExpiry > 0) {
    return {
      isWithinGracePeriod: false,
      gracePeriodDaysRemaining: GRACE_PERIOD_DAYS,
      daysOverdue: 0,
      shouldForcePayment: false
    };
  }
  
  // Subscription has expired
  const daysOverdue = Math.abs(daysUntilExpiry);
  const isWithinGracePeriod = daysUntilGraceEnd > 0;
  
  return {
    isWithinGracePeriod,
    gracePeriodDaysRemaining: Math.max(0, daysUntilGraceEnd),
    daysOverdue,
    shouldForcePayment: !isWithinGracePeriod
  };
};

export const useSubscriptionAccess = () => {
  const { isSuperAdmin, loading: superAdminLoading } = useSuperAdmin();
  const { currentSubscription, loading: subscriptionLoading } = useSubscriptionPlans();
  const location = useLocation();

  return useMemo(() => {
    // Super admin не зависит от подписки — не ждём её загрузки
    const loading = superAdminLoading || (!superAdminLoading && !isSuperAdmin && subscriptionLoading);
    
    // Still loading — разрешаем доступ временно
    if (loading) {
      return {
        hasAccess: true,
        isExpired: false,
        isAdmin: false,
        loading: true,
        shouldRedirect: false,
        allowedPath: '/subscription/payment',
        gracePeriodInfo: null as GracePeriodInfo | null
      };
    }

    // Super admin bypass
    if (isSuperAdmin) {
      return {
        hasAccess: true,
        isExpired: false,
        isAdmin: true,
        loading: false,
        shouldRedirect: false,
        allowedPath: '/subscription/payment',
        gracePeriodInfo: null
      };
    }

    // ⚡ AK-47: действующая подписка = expires_at в будущем или null (бессрочная).
    // Статус ('pending', 'active', 'trial') не является гейтом доступа —
    // это операционный флаг биллинга, а не разрешение на работу с системой.
    const hasValidSubscription = !!currentSubscription && (
      currentSubscription.expires_at === null ||
      new Date(currentSubscription.expires_at) > new Date()
    );
    
    if (hasValidSubscription) {
      const gracePeriodInfo = calculateGracePeriod(currentSubscription.expires_at);
      
      // 🔓 ВСЕГДА даём доступ, только показываем баннер если истекло
      // Не блокируем пользователей полностью - это плохой UX
      return {
        hasAccess: true,
        isExpired: gracePeriodInfo.daysOverdue > 0,
        isAdmin: false,
        loading: false,
        shouldRedirect: false,
        allowedPath: '/subscription/payment',
        gracePeriodInfo
      };
    }

    // Нет подписки — тоже даём доступ с баннером
    return {
      hasAccess: true,
      isExpired: true,
      isAdmin: false,
      loading: false,
      shouldRedirect: false,
      allowedPath: '/subscription/payment',
      gracePeriodInfo: null
    };
  }, [isSuperAdmin, currentSubscription, location.pathname, superAdminLoading, subscriptionLoading]);
};
